import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { useToast } from '../contexts/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import GoogleAuthButton from '../components/GoogleAuthButton';
import { usePageTitle } from '../hooks/usePageTitle';
import { sanitizeRedirect } from '../utils/redirect';
import { trackClick } from '../utils/analytics';

const RegisterPage: React.FC = () => {
  usePageTitle('Sign Up');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, error, clearError, isAuthenticated } = useAuthStore();
  const { plans, fetchPlans, loading: plansLoading } = useSubscriptionStore();
  const { showSuccess, showError } = useToast();

  const planParam = searchParams.get('plan');
  const redirectTo = sanitizeRedirect(searchParams.get('redirect'));

  const avatarColors = [
    '#8B5CF6', '#06B6D4', '#10B981', '#F59E0B',
    '#EF4444', '#EC4899', '#6366F1', '#14B8A6',
  ];

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirm: '',
    name: '',
    avatar_color: avatarColors[Math.floor(Math.random() * avatarColors.length)],
    plan_id: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    if (plans.length > 0) {
      if (planParam) {
        const selectedPlan = plans.find(p => p.name === planParam);
        if (selectedPlan) setFormData(prev => ({ ...prev, plan_id: selectedPlan.id }));
      } else {
        const freePlan = plans.find(p => p.name === 'free');
        if (freePlan) setFormData(prev => ({ ...prev, plan_id: freePlan.id }));
      }
    }
  }, [plans, planParam]);

  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo);
  }, [isAuthenticated, navigate, redirectTo]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) clearError();
  };

  const validateForm = () => {
    if (!formData.email.trim()) { showError('Please enter your email'); return false; }
    if (!formData.email.includes('@')) { showError('Please enter a valid email'); return false; }
    if (!formData.password) { showError('Please enter a password'); return false; }
    if (formData.password.length < 8) { showError('Password must be at least 8 characters'); return false; }
    if (formData.password !== formData.password_confirm) { showError('Passwords do not match'); return false; }
    if (!formData.plan_id) { showError('Please select a subscription plan'); return false; }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;
    trackClick('RegisterPage_button_create_account');
    setIsSubmitting(true);
    try {
      const nameParts = formData.name.trim().split(' ');
      await register({
        email: formData.email,
        password: formData.password,
        password_confirm: formData.password_confirm,
        first_name: nameParts[0] || '',
        last_name: nameParts.slice(1).join(' ') || '',
        avatar_color: formData.avatar_color,
        plan_id: formData.plan_id,
      });
      showSuccess('Account created successfully!');
      navigate(redirectTo);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Decorative */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-500 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-amber-400/30 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute top-2/3 right-1/3 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <Link to="/" onClick={() => trackClick('RegisterPage_logo_navigate_home')} className="flex items-center gap-3 mb-12">
            <img src="/logo.jpeg" alt="Retrosive" className="w-12 h-12 rounded-xl object-contain bg-white p-0.5" />
            <span className="text-2xl font-bold text-white">Retrosive</span>
          </Link>

          <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
            Start running<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-300">
              amazing retrospectives
            </span>
          </h1>

          <p className="text-lg text-orange-50 mb-12 max-w-md">
            Join thousands of teams using Retrosive to improve their workflows and build better products together.
          </p>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
            <p className="text-orange-50 italic">
              "Retrosive transformed how our team does retrospectives. It's intuitive, collaborative, and actually fun to use."
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      </div>

      {/* Right Side - Register Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-6 lg:px-12 xl:px-20 bg-white overflow-y-auto">
        <div className="w-full max-w-md mx-auto">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.jpeg" alt="Retrosive" className="w-8 h-8 rounded-xl object-contain" />
              <span className="text-lg font-bold text-slate-900">Retrosive</span>
            </Link>
          </div>

          <div className="mb-4">
            <h2 className="text-xl font-bold text-slate-900">Create your account</h2>
            <p className="mt-1 text-sm text-slate-500">
              Already have an account?{' '}
              <Link to="/login" onClick={() => trackClick('RegisterPage_link_login')} className="font-medium text-orange-500 hover:text-orange-500">
                Sign in
              </Link>
            </p>
          </div>

          {/* Google Sign Up */}
          <GoogleAuthButton
            text="Continue with Google"
            variant="light"
            redirectTo={redirectTo}
          />

          {/* Divider */}
          <div className="my-4 flex items-center">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="px-4 text-sm text-slate-400">or continue with email</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
                Full name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                placeholder="John Doe"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                placeholder="you@example.com"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all pr-12"
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  onClick={() => {
                    trackClick('RegisterPage_password_toggle');
                    setShowPassword(!showPassword);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="mt-1.5 text-xs text-slate-400">Must be at least 8 characters</p>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="password_confirm" className="block text-sm font-medium text-slate-700 mb-1">
                Confirm password
              </label>
              <input
                id="password_confirm"
                name="password_confirm"
                type="password"
                required
                value={formData.password_confirm}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                placeholder="Confirm your password"
              />
            </div>

            {/* Plan Selection */}
            {plansLoading ? (
              <div className="flex items-center justify-center py-4">
                <LoadingSpinner size="small" />
                <span className="ml-2 text-sm text-slate-500">Loading plans...</span>
              </div>
            ) : plans.length > 0 ? (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Choose your plan
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {plans.map((plan) => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => {
                        trackClick('RegisterPage_plan_select', { event_label: plan.name });
                        setFormData(prev => ({ ...prev, plan_id: plan.id }));
                      }}
                      className={`p-2 rounded-xl border-2 transition-all text-left ${
                        formData.plan_id === plan.id
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-200 hover:border-orange-300'
                      }`}
                    >
                      <span className="font-semibold text-slate-900 block mb-0.5">{plan.display_name}</span>
                      {Number(plan.monthly_price || 0) === 0 ? (
                        <span className="text-sm font-bold text-orange-500">Free</span>
                      ) : (
                        <span className="text-sm font-bold text-slate-900">${plan.monthly_price}/mo</span>
                      )}
                      <p className="text-xs text-slate-500 mt-1">{plan.description}</p>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-400">
                  You can upgrade or downgrade anytime from your subscription settings.
                </p>
              </div>
            ) : null}

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting || plansLoading || !formData.plan_id}
              className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <LoadingSpinner size="small" />
              ) : plansLoading ? (
                'Loading plans...'
              ) : !formData.plan_id ? (
                'Please select a plan'
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <p className="mt-4 text-center text-sm text-slate-500">
            By creating an account, you agree to our{' '}
            <Link to="/terms" onClick={() => trackClick('RegisterPage_link_terms')} className="text-orange-500 hover:underline">Terms</Link> and{' '}
            <Link to="/privacy" onClick={() => trackClick('RegisterPage_link_privacy')} className="text-orange-500 hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
